'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneInput, TextInput } from '@galapagos/design-system/web';
import { isUsableEmail, normalizedEmail, usPhoneDestination } from '../services/identity-fields';
import { SignupRequestError } from '../services/signup-api/signup-api';
import type { SignupSignInContact } from '../stores/signup-flow-store';

export const SIGN_IN_METHODS = ['phone', 'email'] as const;
export type SignInMethod = (typeof SIGN_IN_METHODS)[number];
export const [PHONE_METHOD, EMAIL_METHOD] = SIGN_IN_METHODS;

/** The tab a code channel was sent on. */
export function signInMethodFor(contact: SignupSignInContact | null): SignInMethod {
  return contact?.channel === 'email' ? EMAIL_METHOD : PHONE_METHOD;
}

export interface SignInContactErrors {
  phone?: string;
  email?: string;
}

/** Codes the API answers for a destination it cannot use, shown on the field instead of the page. */
const PHONE_FIELD_CODES = new Set(['invalid_phone_format', 'invalid_phone_country']);
const EMAIL_FIELD_CODES = new Set(['invalid_email_format']);

export interface SignInContactForm {
  method: SignInMethod;
  setMethod: (method: SignInMethod) => void;
  phoneDigits: string;
  setPhoneDigits: (digits: string) => void;
  email: string;
  setEmail: (email: string) => void;
  errors: SignInContactErrors;
  setErrors: (errors: SignInContactErrors) => void;
  clearError: (field: keyof SignInContactErrors) => void;
  /** Where the code goes, or null with the field error to show for the active tab. */
  readContact: () => { contact: SignupSignInContact | null; errors: SignInContactErrors };
  /** Shows a send rejection on its field and returns true, or returns false for a page message. */
  showFieldRejection: (error: unknown) => boolean;
}

/**
 * The phone and email tabs' state, shared by the steps that send a sign-in
 * code (create account and returning login), so both validate and normalize a
 * contact the same way.
 */
export function useSignInContactForm(
  initialMethod: SignInMethod = PHONE_METHOD,
): SignInContactForm {
  const { t } = useTranslation('signup');
  const [method, setMethod] = useState<SignInMethod>(initialMethod);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<SignInContactErrors>({});

  const clearError = (field: keyof SignInContactErrors) =>
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));

  const readContact = () => {
    if (method === PHONE_METHOD) {
      if (phoneDigits === '')
        return { contact: null, errors: { phone: t('account.phoneRequired') } };
      const destination = usPhoneDestination(phoneDigits);
      return destination === null
        ? { contact: null, errors: { phone: t('account.phoneError') } }
        : { contact: { channel: 'sms' as const, destination }, errors: {} };
    }
    if (email.trim() === '')
      return { contact: null, errors: { email: t('account.emailRequired') } };
    return isUsableEmail(email)
      ? { contact: { channel: 'email' as const, destination: normalizedEmail(email) }, errors: {} }
      : { contact: null, errors: { email: t('account.emailError') } };
  };

  const showFieldRejection = (error: unknown) => {
    if (!(error instanceof SignupRequestError) || error.code === undefined) return false;
    if (EMAIL_FIELD_CODES.has(error.code)) {
      setErrors({ email: t('account.emailError') });
      return true;
    }
    if (PHONE_FIELD_CODES.has(error.code)) {
      setErrors({ phone: t('account.phoneError') });
      return true;
    }
    return false;
  };

  return {
    method,
    setMethod,
    phoneDigits,
    setPhoneDigits,
    email,
    setEmail,
    errors,
    setErrors,
    clearError,
    readContact,
    showFieldRejection,
  };
}

/**
 * The active tab's field: a cell phone or an email. Member-entered values, so
 * they sit inside the no-capture marker, kept out of autocapture and click
 * events. Each step passes its own field test ids.
 */
export function SignInContactFields({
  form,
  testIds,
}: {
  form: SignInContactForm;
  testIds: { phone: string; email: string };
}) {
  const { t } = useTranslation('signup');
  return (
    <div
      className="ph-no-capture signup-fields"
      role="group"
      aria-label={form.method === PHONE_METHOD ? t('account.tabs.phone') : t('account.tabs.email')}
    >
      {form.method === PHONE_METHOD ? (
        /* analytics-exempt: forwarded-wrapper — the web PhoneInput tags its own input (ui.phone-input.change). */
        <PhoneInput
          label={t('account.phoneLabel')}
          value={form.phoneDigits}
          onChangeText={(digits) => {
            form.setPhoneDigits(digits);
            form.clearError('phone');
          }}
          name="tel-national"
          errorText={form.errors.phone}
          data-testid={testIds.phone}
        />
      ) : (
        <TextInput
          label={t('account.emailLabel')}
          type="email"
          inputMode="email"
          autoComplete="email"
          name="email"
          value={form.email}
          onChangeText={(next) => {
            form.setEmail(next);
            form.clearError('email');
          }}
          errorText={form.errors.email}
          data-testid={testIds.email}
        />
      )}
    </div>
  );
}
